import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Target, Plus, Trash, CheckCircle, Circle, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/pages/command/ui";

const empty = (clientId, companyId) => ({ client_id: clientId || null, company_id: companyId || null, title: "", target_date: "", done: false, notes: "" });

export function Goals({ clientId, companyId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty(clientId, companyId));
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const params = {}; if (clientId) params.client_id = clientId; if (companyId) params.company_id = companyId;
    try { const { data } = await api.get("/cc/goals", { params }); setRows(data); } catch { /* ignore */ }
  }, [clientId, companyId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditId(null); setForm(empty(clientId, companyId)); setOpen(true); };
  const openEdit = (g) => { setEditId(g.id); setForm({ ...empty(clientId, companyId), ...g, target_date: g.target_date || "" }); setOpen(true); };
  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = { ...form, client_id: clientId || null, company_id: companyId || null };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (editId) await api.put(`/cc/goals/${editId}`, payload); else await api.post("/cc/goals", payload);
      toast.success("Saved"); setOpen(false); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const toggle = async (g) => { try { await api.put(`/cc/goals/${g.id}`, { ...g, done: !g.done }); setRows((r) => r.map((x) => x.id === g.id ? { ...x, done: !x.done } : x)); } catch { toast.error("Failed"); } };
  const remove = async (g) => { if (!window.confirm(`Delete goal "${g.title}"?`)) return; try { await api.delete(`/cc/goals/${g.id}`); setRows((r) => r.filter((x) => x.id !== g.id)); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="goals-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Target size={20} className="text-primary" /><h2 className="text-lg font-medium">Goals &amp; To-dos ({rows.length})</h2></div>
        <Button size="sm" onClick={openNew} data-testid="new-goal-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={16} weight="bold" /> Add goal</Button>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-muted-foreground">No goals yet. Set a goal with a target date.</div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="goals-list">
          {rows.map((g) => (
            <div key={g.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`goal-row-${g.id}`}>
              <button onClick={() => toggle(g)} className="flex items-center gap-3 min-w-0 text-left" data-testid={`toggle-goal-${g.id}`}>
                {g.done ? <CheckCircle size={20} weight="fill" className="text-emerald-500 shrink-0" /> : <Circle size={20} className="text-zinc-600 shrink-0" />}
                <span className="min-w-0">
                  <span className={`text-sm block truncate ${g.done ? "line-through text-muted-foreground" : ""}`}>{g.title}</span>
                  <span className="text-xs text-muted-foreground">{g.target_date ? `by ${fmtDate(g.target_date)}` : "no date"}{g.notes ? ` · ${g.notes}` : ""}</span>
                </span>
              </button>
              <span className="flex items-center gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => openEdit(g)} data-testid={`edit-goal-${g.id}`}><PencilSimple size={16} /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(g)} data-testid={`delete-goal-${g.id}`} className="text-red-300 hover:text-red-200"><Trash size={16} /></Button>
              </span>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit Goal" : "Add Goal"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input data-testid="goal-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
            <div><Label>Target date</Label><Input type="date" data-testid="goal-date-input" value={form.target_date || ""} onChange={(e) => set("target_date", e.target.value)} /></div>
            <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-goal-button" disabled={saving} onClick={save} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
